import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { environment } from '../../../utils';
import { useCurrentSong } from '../../common/Player';

const { REACT_APP_BACKEND_HTTP_URL, REACT_APP_START_DATE, REACT_APP_END_DATE } =
  environment;

const Container = styled.div`
  /* mega lazy way to override body max-width */
  width: 100vw;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  margin: 0;
  padding: 0;
  /* the global setting based on rem makes this dependent on browser font size and I'm way too lazy to fix it properly */
  line-height: 21px;

  background: linear-gradient(
    to right,
    ${(p) => p.theme.color.blueDark},
    ${(p) => p.theme.color.bgLight}
  );
  background-size: cover;
  color: ${(p) => p.theme.color.boldText};
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
`;

const SubContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0;
  width: 50vw;
`;

const ProgramInfo = styled.div`
  flex: 1 1 auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  padding: 0;
  font-size: ${(p) => (p.programOnly ? '24px' : '28px')};
  font-weight: 700;
  color: ${(p) => p.theme.color.boldText};
  line-height: 28px;
`;

const Time = styled.h1`
  padding-top: 16px;
  font-size: 40px;
  font-weight: 700;
  color: ${(p) => p.theme.color.boldText};
  line-height: 28px;
`;

const ProgramTitle = styled.h1`
  font-size: ${(p) => (p.programOnly ? '40px' : '26px')};
  font-weight: 400;
  padding-top: ${(p) => (p.programOnly ? '20px' : '8px')};
  color: ${(p) => p.theme.color.boldText};
  line-height: 36px;
`;

const Author = styled.h1`
  font-size: ${(p) => (p.programOnly ? '26px' : '21px')};
  font-weight: 400;
  padding-top: ${(p) => (p.programOnly ? '20px' : '5px')};
  color: ${(p) => p.theme.color.boldText};
  line-height: 28px;
`;

const Spacer = styled.div`
  flex: 1 1 0;
`;

const Image = styled.img`
  height: 200px;
  width: 200px;
  margin-left: 20px;
  margin-right: 20px;
  align-self: center;
`;

const UpcomingContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  width: 45vw;
`;

const UpcomingTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: ${(p) => p.theme.color.boldText};
  margin: 0;
  padding: 0;
`;

const UpcomingShow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 15px;
  width: 100%;
`;

const UpcomingInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-right: 15px;
  text-align: right;
`;

const UpcomingTime = styled.div`
  font-size: 24px;
  font-weight: 700;
`;

const UpcomingName = styled.div`
  font-size: 20px;
  font-weight: 400;
`;

const UpcomingAuthor = styled.div`
  font-size: 16px;
  font-weight: 400;
`;

const UpcomingImage = styled.img`
  width: 85px;
  height: 85px;
  object-fit: cover;
`;

const PlaceholderImage = ({ alt }) => (
  <Image
    src={`${environment.REACT_APP_STATIC_URL}/img/2026/ohjelmat/placeholder.jpg`}
    alt={alt}
  />
);

const Currently = () => {
  const { title, artist } = useCurrentSong();

  const [next, setNext] = useState();
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    if (Date.parse(REACT_APP_START_DATE) && Date.parse(REACT_APP_END_DATE)) {
      const fetchProgrammes = async () => {
        try {
          const resp = await fetch(`${REACT_APP_BACKEND_HTTP_URL}/programmes`);
          const data = await resp.json();
          if (!data || !Array.isArray(data)) {
            console.error('Programme data invalid. Data: ', data);
            return;
          }

          let last;
          let upc = [];
          let now = new Date();
          for (let program of data) {
            if (new Date(program.end) > now) {
              if (!last) {
                last = program;
              } else if (upc.length < 2) {
                upc.push(program);
              }
              if (last && upc.length >= 2) break;
            }
          }

          if (!last) return;

          function removeHtml(text) {
            if (!text) return text;
            const div = document.createElement('div');
            div.innerHTML = text;
            return div.textContent.trim();
          }

          const clean = (p) => {
            let cp = { ...p };
            cp.start = new Date(cp.start);
            cp.end = new Date(cp.end);
            if (
              /<br>|<span>/.test(
                `${cp.image}${cp.description}${cp.team}${cp.genre}`
              )
            ) {
              if (cp.image) cp.image = removeHtml(cp.image);
              if (cp.description) cp.description = removeHtml(cp.description);
              if (cp.team) cp.team = removeHtml(cp.team);
              if (cp.genre) cp.genre = removeHtml(cp.genre);
            }
            return cp;
          };

          setNext(clean(last));
          setUpcoming(upc.map(clean));
        } catch (error) {
          console.error(error);
        }
      };
      fetchProgrammes();
    }
  }, []);

  const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });

  if (!next) {
    return <Container />;
  }

  const img =
    next && next.image && next.image.trim() ? (
      <Image src={next.image} alt={next.title} />
    ) : (
      <PlaceholderImage alt={next.title} />
    );

  const showSong = !!(artist && title);

  const time = `${dateTimeFormat.format(next.start)} \u2012 ${dateTimeFormat.format(next.end)}`;

  return (
    <Container>
      <SubContainer>
        <ProgramInfo>
          {showSong && (
            <>
              <PageTitle>Nyt soi:</PageTitle>
              <ProgramTitle>
                {artist}
                {' \u2013 '}
                {title}
              </ProgramTitle>
              <Spacer />
            </>
          )}
          <PageTitle programOnly={!showSong}>
            {new Date() > next.start ? 'Studiossa:' : 'Seuraavaksi studiossa:'}
          </PageTitle>
          {!showSong && <Time>{time}</Time>}
          <ProgramTitle programOnly={!showSong}>
            {showSong && `${time} `}
            {next.title}
          </ProgramTitle>
          <Author programOnly={!showSong}>{next.team}</Author>
        </ProgramInfo>
        {img}
      </SubContainer>
      
      <UpcomingContainer>
        {upcoming.length > 0 && <UpcomingTitle>Tulossa:</UpcomingTitle>}
        {upcoming.map((prog, idx) => (
          <UpcomingShow key={idx}>
            <UpcomingInfo>
              <UpcomingTime>
                {dateTimeFormat.format(prog.start)} {'\u2012'} {dateTimeFormat.format(prog.end)}
              </UpcomingTime>
              <UpcomingName>{prog.title}</UpcomingName>
              <UpcomingAuthor>{prog.team}</UpcomingAuthor>
            </UpcomingInfo>
            {prog.image && prog.image.trim() ? (
              <UpcomingImage src={prog.image} alt={prog.title} />
            ) : (
              <UpcomingImage
                src={`${environment.REACT_APP_STATIC_URL}/img/2026/ohjelmat/placeholder.jpg`}
                alt={prog.title}
              />
            )}
          </UpcomingShow>
        ))}
        <Spacer />
      </UpcomingContainer>
    </Container>
  );
};

export default Currently;
